import React from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import "./css/editor.css";
import { addPresenzaCrossCorso } from "../services/addPresenzaCrossCorso";
import Cookies from "universal-cookie";
import FixButton from "./Button";
import EditRecuperoLezioneWidget from "./EditRecuperoLezioneWidget";
import PropostaRecuperoLezione from "./PropostaRecuperoLezioneWidget";
import moment from "moment"; // Importo moment per formattare le date
import EventIcon from "@material-ui/icons/Event";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import NoteIcon from "@material-ui/icons/Note";

// Imports
import getPropostaRecupero from "../services/getPropostaRecupero";
import getLezioneRecuperata from "../services/getLezioneRecuperata";
import getRichiestaRecupero from "../services/getRichiestaRecupero";
import getRichiestaRecuperoCompleta from "../services/getRichiestaRecuperoCompleta";
import createRichiestaRecuperoStudente from "../services/createRichiestaRecuperoStudente";

// Funzione helper per formattare le date
const formatDate = (dateString) => {
  if (!dateString) return "";

  // Se la stringa contiene già "ore" significa che è un label formattato dalla proposta
  // Es: "15/01/2025 ore 10:00 - Doppiaggio"
  if (dateString.includes(" ore ")) {
    // Estrai solo la parte della data (tutto prima di " ore ")
    const datePart = dateString.split(" ore ")[0];
    // Verifica se è già nel formato DD/MM/YYYY
    if (datePart.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
      return datePart;
    }
    // Altrimenti prova a parsare con moment
    const parsed = moment(datePart, "DD/MM/YYYY");
    return parsed.isValid() ? parsed.format("DD/MM/YYYY") : datePart;
  }

  // Se la data è già nel formato DD/MM/YYYY, HH:mm o DD/MM/YYYY (già formattata), ritornala
  if (dateString.match(/^\d{2}\/\d{2}\/\d{4}(,?\s*\d{2}:\d{2})?$/)) {
    return dateString.split(",")[0].trim(); // Ritorna solo la parte data
  }

  // Per le date normali, prova multipli formati
  const parsed = moment(
    dateString,
    [
      "YYYY-MM-DD HH:mm:ss",
      "YYYY-MM-DD HH:mm",
      "YYYY-MM-DDTHH:mm:ss",
      "YYYY-MM-DDTHH:mm",
      "YYYY-MM-DD",
      "DD/MM/YYYY, HH:mm",
      "DD/MM/YYYY HH:mm:ss",
      "DD/MM/YYYY HH:mm",
      "DD/MM/YYYY",
    ],
    true, // strict parsing
  );

  if (parsed.isValid()) {
    return parsed.format("DD/MM/YYYY");
  }

  // Fallback: prova con Date nativo
  try {
    const nativeDate = new Date(dateString.replace(" ", "T"));
    if (!isNaN(nativeDate.getTime())) {
      return nativeDate.toLocaleDateString("it-IT", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    }
  } catch (e) {
    console.error("formatDate: Errore parsing data:", e, dateString);
  }

  // Ultimo fallback: ritorna la stringa originale
  return dateString;
};

export default class DetailsDialog extends React.Component {
  constructor(props) {
    super(props);

    console.log("[DetailsDialog CONSTRUCTOR] props.details:", props.details);
    if (props.details && props.details.length > 0) {
      console.log(
        "[DetailsDialog CONSTRUCTOR] Ultima riga:",
        props.details[props.details.length - 1],
      );
    }

    // Funzione helper per decodificare le entità HTML nelle note
    this.decodeHtmlEntities = (text) => {
      if (!text) return "";
      const textarea = document.createElement("textarea");
      textarea.innerHTML = text;
      return textarea.value;
    };

    this.state = {
      editorState: EditorState.createEmpty(),
      subject: "",
      openDetails: !props.showExams,
      openExams: !!props.showExams,
      showEditRecupero: false,
      showPropostaRecupero: false,
      editRecuperoOnlyNotes: false, // Flag per modalità "solo note" o completa

      recuperi: {},
      proposte: {},
      proposteComplete: {},

      det: null,
      currentNote: "",
      currentIdSessione: null, // AGGIUNTO: ID sessione per recuperi parziali (mezza giornata)
    };
  }

  componentDidMount() {
    console.log("DetailsDialog: componentDidMount - Caricamento iniziale dati");
    // Carica solo i dati interni, senza chiamare onReload per non bloccare il modal
    this.loadComponent();
  }

  componentDidUpdate(prevProps) {
    // Log per debug
    if (prevProps.details !== this.props.details) {
      console.log("[DetailsDialog componentDidUpdate] Props.details CAMBIATE!");
      console.log(
        "[DetailsDialog componentDidUpdate] prevProps.details ultima riga:",
        prevProps.details?.[prevProps.details.length - 1],
      );
      console.log(
        "[DetailsDialog componentDidUpdate] this.props.details ultima riga:",
        this.props.details?.[this.props.details.length - 1],
      );
    }

    // Se i details sono cambiati, ricarica i dati
    if (prevProps.details !== this.props.details) {
      this.loadComponent();
    }

    // ✅ FIX: Se il modal viene riaperto (show passa da false a true), ricarica i dati
    // Questo permette di vedere i recuperi aggiornati quando si chiude e riapre il modal
    // senza dover uscire e rientrare dal corso
    if (!prevProps.show && this.props.show) {
      console.log(
        "DetailsDialog: Modal riaperto, ricaricando dati recuperi aggiornati",
      );
      // Ricarica sia i dati interni del modal che i dati dal backend (recuperi del corso corrente)
      this.reloadRecupero();
    }
  }

  reloadRecupero = async () => {
    console.log("DetailsDialog: reloadRecupero chiamato");
    await this.loadComponent();
    // Chiama onReload del componente padre solo se definito (per aggiornare le statistiche)
    if (this.props.onReload) {
      console.log("DetailsDialog: Chiamando onReload del componente padre");
      await this.props.onReload();
    }
  };

  printAttendanceSummary = () => {
    const cookies = new Cookies();
    const currentCourse = cookies.get("currentCourse", { path: "/" });
    const username = cookies.get("username", { path: "/" });
    const token = cookies.get("token", { path: "/" });

    // Prendi l'ID studente dai details se disponibile
    const studentId =
      this.props.details && this.props.details.length > 0
        ? this.props.details[0][5] // id_studente nella posizione 5
        : null;

    if (studentId) {
      cookies.set("currentStudente", studentId, { path: "/" });
      window.location.href = "/#/app/pdfRiepilogoPresenze";
    } else {
      alert("Errore: impossibile identificare lo studente per la stampa");
    }
  };
  async loadComponent() {
    console.log("DetailsDialog: loadComponent chiamato");
    const cookies = new Cookies();
    const username = cookies.get("username", { path: "/" });
    const token = cookies.get("token", { path: "/" });

    // ✅ FIX: Ottieni l'ID studente dai details passati come props, NON dalla cookie
    // La cookie può contenere l'ID di un altro studente se hai aperto altri modal prima
    const detailItems = this.props.details || [];
    const id_studente =
      detailItems.length > 0 && detailItems[0][5]
        ? detailItems[0][5] // id_studente è sempre nella posizione 5
        : cookies.get("currentStudente", { path: "/" }); // Fallback alla cookie solo se non trovato nei details

    console.log(
      "DetailsDialog: Dettagli lezioni complete:",
      detailItems.length,
      "elementi",
    );
    console.log(`[DetailsDialog] ID Studente corrente: ${id_studente}`);

    // NUOVO: Crea una mappa id_richiesta_recupero -> id_sessione dai detailItems
    const sessioniMapFromDetails = {};
    detailItems.forEach((det) => {
      const idRichiestaRecupero = det[3]; // posizione 3
      const idSessione = det[6]; // posizione 6
      if (idRichiestaRecupero && idSessione) {
        sessioniMapFromDetails[idRichiestaRecupero] = idSessione;
        console.log(
          `[DetailsDialog] Mappato recupero ${idRichiestaRecupero} -> sessione ${idSessione}`,
        );
      }
    });

    const recPromises = detailItems.map(async (det) => {
      const id = det[3];
      if (!id) return null;

      const res = await getLezioneRecuperata(username, token, id);
      return { id, data: res };
    });

    const recResults = await Promise.all(recPromises);

    const recuperiMap = {};
    for (const item of recResults) {
      if (item && item.id) {
        // SECURITY FIX: Verifica che il recupero appartenga allo studente corrente
        if (item.data.id_studente && item.data.id_studente != id_studente) {
          console.warn(
            `[DetailsDialog] ⚠️ SCARTATO recupero ${item.id} - appartiene a studente ${item.data.id_studente}, non ${id_studente}`,
          );
          continue; // Salta questo recupero
        }

        recuperiMap[item.id] = item.data;
        // AGGIUNTO: Se il backend non restituisce id_sessione, usa quello dalla mappa
        if (!item.data.id_sessione && sessioniMapFromDetails[item.id]) {
          recuperiMap[item.id].id_sessione = sessioniMapFromDetails[item.id];
          console.log(
            `[DetailsDialog] Recupero ${item.id}: id_sessione ${
              sessioniMapFromDetails[item.id]
            } aggiunto dalla mappa locale`,
          );
        }
      }
    }

    // 2) fetch the proposte
    const propPromises = detailItems.map(async (det) => {
      const id = det[3];
      if (!id) return null;

      const res = await getPropostaRecupero(username, token, id);
      return { id, data: res };
    });

    const propResults = await Promise.all(propPromises);

    const proposteMap = {};
    for (const item of propResults) {
      if (item && item.id) {
        // SECURITY FIX: Verifica che la proposta appartenga allo studente corrente
        if (item.data.id_studente && item.data.id_studente != id_studente) {
          console.warn(
            `[DetailsDialog] ⚠️ SCARTATA proposta ${item.id} - appartiene a studente ${item.data.id_studente}, non ${id_studente}`,
          );
          continue; // Salta questa proposta
        }

        proposteMap[item.id] = item.data;
        // AGGIUNTO: Se il backend non restituisce id_sessione, usa quello dalla mappa
        if (!item.data.id_sessione && sessioniMapFromDetails[item.id]) {
          proposteMap[item.id].id_sessione = sessioniMapFromDetails[item.id];
          console.log(
            `[DetailsDialog] Proposta ${item.id}: id_sessione ${
              sessioniMapFromDetails[item.id]
            } aggiunto dalla mappa locale`,
          );
        }
      }
    }

    // Update state
    console.log(
      "DetailsDialog: Aggiornando stato con",
      Object.keys(recuperiMap).length,
      "recuperi",
    );
    this.setState({
      recuperi: recuperiMap,
      proposte: proposteMap,
    }); // Log della struttura dei dati delle proposte e recuperi
    console.log("Struttura proposte:", proposteMap);
    console.log("Struttura recuperi:", recuperiMap);
  }

  onContentStateChange = (contentState) => {
    console.log("as HTML:", draftToHtml(contentState));
    this.setState({ contentState });
  };

  manageSelection({ selected }) {
    if (selected === "mainPage") {
      this.setState({ openDetails: true, openExams: false });
    } else if (selected === "examsPage") {
      this.setState({ openDetails: false, openExams: true });
    }
  }

  // Funzione per aprire il modal per MODIFICARE LE NOTE
  openEditNoteRecuperoModal = async (id, detailItem, currentNote = "") => {
    // Recupera la nota dallo state (stesso modo della sezione visualizzazione)
    let noteToPass = currentNote;
    if (!noteToPass && id) {
      const recupero = this.state.recuperi[id];
      const proposta = this.state.proposte[id];

      console.log("[openEditNoteRecuperoModal] ID:", id);
      console.log(
        "[openEditNoteRecuperoModal] Recupero dallo state:",
        recupero,
      );
      console.log(
        "[openEditNoteRecuperoModal] Proposta dallo state:",
        proposta,
      );
      console.log("[openEditNoteRecuperoModal] Note cercate:", {
        recupero_note: recupero?.note,
        recupero_note_recupero: recupero?.note_recupero,
        proposta_note: proposta?.note,
        proposta_note_recupero: proposta?.note_recupero,
      });

      noteToPass =
        recupero?.note ||
        recupero?.note_recupero ||
        proposta?.note ||
        proposta?.note_recupero ||
        "";
    }

    this.setState({
      showEditRecupero: true,
      det: id,
      currentNote: noteToPass,
      editRecuperoOnlyNotes: true, // Flag per indicare modalità "solo note"
    });
  };

  // Funzione per aprire il modal per RECUPERARE LA LEZIONE (selezione corso + lezione + note)
  openEditRecuperoModal = async (id, detailItem, currentNote = "") => {
    // Recupera la nota dallo state (stesso modo della sezione visualizzazione)
    let noteToPass = currentNote;
    if (!noteToPass && id) {
      const recupero = this.state.recuperi[id];
      const proposta = this.state.proposte[id];

      console.log("[openEditRecuperoModal] ID:", id);
      console.log("[openEditRecuperoModal] Recupero dallo state:", recupero);
      console.log("[openEditRecuperoModal] Proposta dallo state:", proposta);
      console.log("[openEditRecuperoModal] Note cercate:", {
        recupero_note: recupero?.note,
        recupero_note_recupero: recupero?.note_recupero,
        proposta_note: proposta?.note,
        proposta_note_recupero: proposta?.note_recupero,
      });

      noteToPass =
        recupero?.note ||
        recupero?.note_recupero ||
        proposta?.note ||
        proposta?.note_recupero ||
        "";

      console.log(
        "[openEditRecuperoModal] Nota finale da passare:",
        noteToPass,
      );
    }

    // Se non esiste un ID richiesta recupero, creane uno
    if (!id && detailItem) {
      const requestId = await this.createRichiestaRecuperoIfNeeded(detailItem);
      if (requestId) {
        // Estrai id_sessione dal detailItem (posizione 6)
        const idSessione = detailItem[6] || null;

        this.setState({
          showEditRecupero: true,
          det: requestId,
          currentNote: noteToPass,
          editRecuperoOnlyNotes: false, // Flag per indicare modalità completa (corso + lezione + note)
          currentIdSessione: idSessione, // Memorizza id_sessione per recuperi parziali
        });
        return;
      }
    }

    // Estrai id_sessione dal detailItem (posizione 6)
    const idSessione = detailItem ? detailItem[6] || null : null;

    this.setState({
      showEditRecupero: true,
      det: id,
      currentNote: noteToPass,
      editRecuperoOnlyNotes: false, // Flag per indicare modalità completa
      currentIdSessione: idSessione, // Memorizza id_sessione per recuperi parziali
    });
  };

  openEditProponiRecuperoModal = async (id, detailItem) => {
    // Se non esiste un ID richiesta recupero, creane uno
    if (!id && detailItem) {
      const requestId = await this.createRichiestaRecuperoIfNeeded(detailItem);
      if (requestId) {
        this.setState({ showPropostaRecupero: true, det: requestId });
        return;
      }
    }
    this.setState({ showPropostaRecupero: true, det: id });
  };

  generateRecuperoRequest = async (detailItem) => {
    try {
      const cookies = new Cookies();
      const username = cookies.get("username", { path: "/" });
      const token = cookies.get("token", { path: "/" });

      // Estrai le informazioni dal detailItem
      // detailItem è nel formato [data, argomento, status, id_richiesta_recupero, id_lezione, id_studente, id_sessione]
      const idStudente = detailItem[5]; // id_studente nella posizione 5
      const idLezioneOriginale = detailItem[4]; // id_lezione nella posizione 4
      const idSessione = detailItem[6]; // id_sessione nella posizione 6

      // IMPORTANTE: Rimuovi il prefisso "prev_" se presente (assenze da corso precedente)
      const idLezione =
        typeof idLezioneOriginale === "string" &&
        idLezioneOriginale.startsWith("prev_")
          ? idLezioneOriginale.replace("prev_", "")
          : idLezioneOriginale;

      console.log("[generateRecuperoRequest] Dati estratti:", {
        idStudente,
        idLezioneOriginale,
        idLezione,
        idSessione,
        detailItem,
      });

      if (!idStudente || !idLezione) {
        console.error(
          "Informazioni mancanti per creare la richiesta di recupero",
          {
            idStudente,
            idLezione,
            idSessione,
            detailItem,
          },
        );
        alert(
          "Errore: informazioni mancanti per creare la richiesta di recupero",
        );
        return;
      }

      console.log(
        "[generateRecuperoRequest] Chiamata a createRichiestaRecuperoStudente con:",
        {
          username,
          idStudente,
          idLezione,
          idSessione,
        },
      );

      const result = await createRichiestaRecuperoStudente(
        username,
        token,
        idStudente,
        idLezione,
        idSessione, // Passa anche id_sessione
      );

      console.log("Risultato generateRecuperoRequest:", result);

      // Gestione caso "Richiesta già presente"
      if (result?.error && result.error.includes("già presente")) {
        console.log(
          "[generateRecuperoRequest] Richiesta già esistente, ricarico dati per mostrare pulsanti corretti",
        );

        // Ricarica i dati per mostrare i pulsanti corretti
        if (this.props.onReload) {
          await this.props.onReload();
        }

        await new Promise((resolve) => setTimeout(resolve, 200));
        await this.loadComponent();
        this.forceUpdate();

        // Non mostrare errore, ma informazione
        alert(
          "ℹ️ RICHIESTA GIÀ PRESENTE\n\n" +
            "Esiste già una richiesta di recupero per questa assenza.\n\n" +
            "Puoi procedere con:\n" +
            "• Proponi recupero - per proporre una data\n" +
            "• Recupera - per registrare il recupero effettuato\n" +
            "• Note - per aggiungere annotazioni",
        );
        return;
      }

      if (result && result.status === "success") {
        alert(
          "Richiesta di recupero generata con successo!\n\n" +
            "I pulsanti 'Proponi recupero' e 'Recupera' sono ora disponibili.",
        );

        // Ricarica immediatamente i dati dal componente padre
        // Questo aggiornerà i details con il nuovo id_recupero_lezioni
        if (this.props.onReload) {
          await this.props.onReload();
        }

        // Aspetta che i dati siano aggiornati e poi ricarica lo stato locale
        await new Promise((resolve) => setTimeout(resolve, 200));
        await this.loadComponent();
        this.forceUpdate();
      } else {
        console.error(
          "Errore nella generazione della richiesta di recupero:",
          result,
        );
        alert(
          "Errore nella generazione della richiesta di recupero: " +
            (result?.error || "Errore sconosciuto"),
        );
      }
    } catch (error) {
      console.error(
        "Errore nella generazione della richiesta di recupero:",
        error,
      );
      alert("Errore nella generazione della richiesta di recupero");
    }
  };

  // Nuova funzione per recuperare direttamente un'assenza precedente
  recuperaAssenzaPrecedente = async (detailItem) => {
    try {
      const cookies = new Cookies();
      const username = cookies.get("username", { path: "/" });
      const token = cookies.get("token", { path: "/" });

      const idStudente = detailItem[5]; // id_studente nella posizione 5
      const idLezioneOriginale = detailItem[4]; // id_lezione originale nella posizione 4

      // Rimuovi il prefisso "prev_" se presente
      const idLezione =
        typeof idLezioneOriginale === "string" &&
        idLezioneOriginale.startsWith("prev_")
          ? idLezioneOriginale.replace("prev_", "")
          : idLezioneOriginale;

      console.log(
        `[recuperaAssenzaPrecedente] Recupero assenza per studente ${idStudente}, lezione ${idLezione}`,
      );

      if (!idStudente || !idLezione) {
        console.error("Informazioni mancanti per il recupero", {
          idStudente,
          idLezione,
          detailItem,
        });
        alert("Errore: informazioni mancanti per il recupero dell'assenza");
        return;
      }

      // Conferma dall'utente
      const conferma = window.confirm(
        "🔄 CONFERMA RECUPERO ASSENZA PRECEDENTE\n\n" +
          "Stai per segnare come RECUPERATA questa assenza da un corso precedente.\n\n" +
          "Questo significa che:\n" +
          "• L'assenza sarà rimossa dal conteggio totale\n" +
          "• Il recupero sarà registrato nel sistema\n" +
          "• L'operazione è reversibile\n\n" +
          "Vuoi procedere?",
      );

      if (!conferma) {
        return;
      }

      // Chiama l'API per segnare come presente (recuperata)
      const result = await addPresenzaCrossCorso(
        username,
        token,
        idLezione,
        idStudente,
        1,
      ); // presenza = 1 (recuperata)

      console.log(
        `[recuperaAssenzaPrecedente] Risultato completo API:`,
        result,
      );
      console.log(`[recuperaAssenzaPrecedente] Tipo risultato:`, typeof result);
      console.log(
        `[recuperaAssenzaPrecedente] Keys risultato:`,
        result ? Object.keys(result) : "result è null/undefined",
      );
      console.log(`[recuperaAssenzaPrecedente] result.result:`, result?.result);
      console.log(
        `[recuperaAssenzaPrecedente] result serializzato:`,
        JSON.stringify(result, null, 2),
      );

      // Controlla se result è un oggetto valido
      if (!result) {
        console.error(`[recuperaAssenzaPrecedente] Risposta null/undefined`);
        alert("❌ ERRORE: Nessuna risposta dall'API");
        return;
      }

      if (result && result.result === "ok") {
        alert(
          "✅ ASSENZA RECUPERATA CON SUCCESSO!\n\n" +
            "L'assenza da corso precedente è stata segnata come recuperata.\n" +
            "Il conteggio delle assenze è stato aggiornato automaticamente.\n\n" +
            result.message || "Recupero completato.",
        );

        // Ricarica i dati del modal per riflettere le modifiche
        if (this.props.onRefresh) {
          this.props.onRefresh();
        }

        // Chiudi il modal per forzare il refresh
        this.props.onClose();
      } else {
        console.error("Errore nel recupero dell'assenza:", result);
        console.error(
          "[recuperaAssenzaPrecedente] Dettagli errore - result.result:",
          result?.result,
        );
        console.error(
          "[recuperaAssenzaPrecedente] Dettagli errore - result.error:",
          result?.error,
        );
        console.error(
          "[recuperaAssenzaPrecedente] Oggetto completo errore:",
          JSON.stringify(result, null, 2),
        );

        // Gestione specifica per errori database
        const errorMessage = result?.error || "Errore sconosciuto";
        const isDbError =
          errorMessage.includes("SQLSTATE") ||
          errorMessage.includes("Column not found");

        if (isDbError) {
          alert(
            "❌ ERRORE DATABASE\n\n" +
              "Si è verificato un errore nella struttura del database.\n" +
              "L'API sta cercando di accedere a una colonna che non esiste.\n\n" +
              "⚠️ AZIONE RICHIESTA: Contatta il supporto tecnico\n" +
              "Il problema deve essere risolto lato backend.\n\n" +
              "Dettagli tecnici:\n" +
              errorMessage,
          );
        } else {
          alert(
            "❌ ERRORE NEL RECUPERO\n\n" +
              "Non è stato possibile completare il recupero dell'assenza.\n\n" +
              "Errore: " +
              errorMessage +
              "\n\nStatus: " +
              (result?.result || "Sconosciuto"),
          );
        }
      }
    } catch (error) {
      console.error("Errore nel recupero dell'assenza precedente:", error);
      alert("Errore nel recupero dell'assenza precedente: " + error.message);
    }
  };

  createRichiestaRecuperoIfNeeded = async (detailItem) => {
    try {
      const cookies = new Cookies();
      const username = cookies.get("username", { path: "/" });
      const token = cookies.get("token", { path: "/" });

      // Estrai le informazioni dal detailItem
      // detailItem è nel formato [data, argomento, status, id_richiesta_recupero, id_lezione, id_studente]
      const idStudente = detailItem[5]; // id_studente nella posizione 5
      const idLezioneOriginale = detailItem[4]; // id_lezione nella posizione 4

      // IMPORTANTE: Rimuovi il prefisso "prev_" se presente (assenze da corso precedente)
      const idLezione =
        typeof idLezioneOriginale === "string" &&
        idLezioneOriginale.startsWith("prev_")
          ? idLezioneOriginale.replace("prev_", "")
          : idLezioneOriginale;

      if (!idStudente || !idLezione) {
        console.error(
          "Informazioni mancanti per creare la richiesta di recupero",
          {
            idStudente,
            idLezioneOriginale,
            idLezione,
            detailItem,
          },
        );
        alert(
          "Errore: informazioni mancanti per creare la richiesta di recupero",
        );
        return null;
      }

      const result = await createRichiestaRecuperoStudente(
        username,
        token,
        idStudente,
        idLezione,
      );

      console.log("Risultato createRichiestaRecuperoStudente:", result);

      if (result && result.status === "success") {
        // Ricarica i dati per aggiornare la vista
        await this.loadComponent();

        // Notifica il parent per aggiornare anche i suoi dati
        if (this.props.onRefresh) {
          this.props.onRefresh();
        }

        // Poiché sendRichiestaRecupero.php non restituisce l'ID,
        // dobbiamo fare una ricerca per trovare l'ID della richiesta creata
        return this.findRichiestaRecuperoId(idStudente, idLezione);
      } else {
        console.error(
          "Errore nella creazione della richiesta di recupero:",
          result,
        );
        alert(
          "Errore nella creazione della richiesta di recupero: " +
            (result?.error || "Errore sconosciuto"),
        );
        return null;
      }
    } catch (error) {
      console.error(
        "Errore nella creazione della richiesta di recupero:",
        error,
      );
      alert("Errore nella creazione della richiesta di recupero");
      return null;
    }
  };

  findRichiestaRecuperoId = async (idStudente, idLezione) => {
    try {
      const cookies = new Cookies();
      const username = cookies.get("username", { path: "/" });
      const token = cookies.get("token", { path: "/" });

      // Usa il servizio esistente per trovare la richiesta di recupero
      const richiestaInfo = await getRichiestaRecupero(
        username,
        token,
        idStudente,
        idLezione,
      );

      if (richiestaInfo && richiestaInfo.id) {
        return richiestaInfo.id;
      }

      // Se non trovato, restituisci un ID fittizio per continuare il flusso
      return "temp_id";
    } catch (error) {
      console.error(
        "Errore nel trovare l'ID della richiesta di recupero:",
        error,
      );
      return "temp_id";
    }
  };

  render() {
    return (
      <div style={{ display: this.props.show ? "block" : "none" }}>
        <div style={container}>
          <div style={mainClass}>
            <div
              style={closeBtn}
              onClick={() => {
                this.props.onClose();
              }}
            >
              X
            </div>
            <div style={title}>{this.props.studentDetails}</div>

            {this.props.retired && (
              <div style={subtitle}>Studente ritirato da questo corso</div>
            )}

            {/* Mostra le tab solo se non è specificata una singola modalità */}
            {!this.props.singleMode ? (
              <>
                <div style={selectionDiv}>
                  {this.state.openDetails ? (
                    <div style={{ ...selectionButton, color: "#f58320" }}>
                      <b>{this.props.title}</b>
                    </div>
                  ) : (
                    <div
                      style={selectionButton}
                      onClick={() =>
                        this.manageSelection({ selected: "mainPage" })
                      }
                    >
                      {this.props.title}
                    </div>
                  )}

                  {this.state.openExams ? (
                    <div style={{ ...selectionButton, color: "#f58320" }}>
                      <b>Dettaglio esami</b>
                    </div>
                  ) : (
                    <div
                      style={selectionButton}
                      onClick={() =>
                        this.manageSelection({ selected: "examsPage" })
                      }
                    >
                      Dettaglio esami
                    </div>
                  )}
                </div>

                <div
                  style={{
                    height: "1px",
                    width: "90%",
                    marginTop: "-10px",
                    marginLeft: "5%",
                    backgroundColor: "#BDBDBD",
                  }}
                ></div>
              </>
            ) : (
              <>
                {/* Mostra solo il titolo della sezione attiva, centrato */}
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "1.2rem",
                    fontWeight: "bold",
                    color: "#f58320",
                    marginTop: "25px",
                    marginBottom: "20px",
                  }}
                >
                  {this.state.openDetails
                    ? this.props.title
                    : "Dettaglio esami"}
                </div>
                <div
                  style={{
                    height: "1px",
                    width: "90%",
                    marginTop: "-10px",
                    marginLeft: "5%",
                    backgroundColor: "#BDBDBD",
                  }}
                ></div>
              </>
            )}

            <div style={{ marginTop: "25px" }}>
              {this.state.openExams &&
                (this.props.exams || []).map((exam) => (
                  <React.Fragment key={exam[0]}>
                    <div style={infoDiv}>
                      <div style={examDescDiv}>{exam[0]}</div>
                      <div style={examAvgDiv}>
                        <b>{exam[1]}</b>
                      </div>
                    </div>
                    <div
                      style={{
                        height: "1px",
                        width: "80%",
                        marginTop: "8px",
                        marginLeft: "10%",
                        backgroundColor: "#D8D8D8",
                      }}
                    ></div>
                  </React.Fragment>
                ))}
              {this.state.openDetails &&
                (() => {
                  // Filtra solo le assenze (incluse quelle precedenti, recuperate e tutte le varianti)
                  console.log(
                    `[DetailsDialog RENDER] this.props.details:`,
                    this.props.details,
                  );
                  console.log(
                    `[DetailsDialog RENDER] this.props.details[0]:`,
                    this.props.details?.[0],
                  );
                  console.log(
                    `[DetailsDialog RENDER] this.props.details[${this.props
                      .details?.length - 1}]:`,
                    this.props.details?.[this.props.details.length - 1],
                  );
                  console.log(
                    `[DetailsDialog RENDER] this.state.openDetails:`,
                    this.state.openDetails,
                  );

                  const assenze = (this.props.details || []).filter(
                    (det) =>
                      det[2] &&
                      (det[2].includes("ASSENTE") ||
                        det[2].includes("RECUPERATA")),
                  );

                  console.log(
                    `[DetailsDialog RENDER] Assenze filtrate:`,
                    assenze.length,
                  );
                  if (assenze.length > 0) {
                    console.log(
                      `[DetailsDialog RENDER] Prima assenza:`,
                      assenze[0],
                    );
                  }

                  // Se non ci sono assenze, mostra un messaggio
                  if (assenze.length === 0) {
                    return (
                      <React.Fragment key="nessuna-assenza">
                        <div style={infoDivPresenze}>
                          <div style={leftText}>Nessuna assenza</div>
                          <div style={rightText}>
                            <b>✓</b>
                          </div>
                        </div>
                        <div style={separatorStyle}></div>
                      </React.Fragment>
                    );
                  }

                  // Altrimenti mostra solo le assenze
                  console.log(
                    `[DetailsDialog RENDER] Renderizzando ${assenze.length} assenze con map`,
                  );
                  return assenze.map((det, index) => {
                    console.log(
                      `[DetailsDialog RENDER MAP] Riga ${index}:`,
                      det,
                    );
                    const id = det[3];
                    const idSessione = det[6]; // Prendi id_sessione da detailItem[6]
                    const recupero = id ? this.state.recuperi[id] : null;
                    const proposta = id ? this.state.proposte[id] : null;
                    console.log(
                      `[DetailsDialog RENDER MAP] Riga ${index} - recupero:`,
                      recupero,
                      "proposta:",
                      proposta,
                    );

                    // CONTROLLO SESSIONE: Una lezione è considerata recuperata SOLO se:
                    // 1. Ha un recupero effettivo con result "ok"
                    // 2. L'id_sessione del recupero corrisponde a quello della riga corrente
                    const recuperoHaSessioneCorretta = recupero?.id_sessione
                      ? recupero.id_sessione === idSessione
                      : true; // Se non c'è id_sessione nel recupero, accetta (retrocompatibilità)

                    const isRecuperataViaRecupero =
                      recupero?.result === "ok" &&
                      recuperoHaSessioneCorretta &&
                      (recupero?.data ||
                        recupero?.data_recupero ||
                        recupero?.id_lezione_recupero);

                    // RIMOSSO: Non considerare più le proposte accettate come recuperi automatici
                    // const isRecuperataViaProposta = proposta?.result === "ok" && proposta?.stato === 1;

                    const isRecuperata = isRecuperataViaRecupero; // Solo recuperi effettivi
                    // ✅ FIX: Riconosci anche "RECUPERATA ✅" come assenza (recuperata)
                    const isAssenza =
                      det[2] &&
                      (det[2].includes("ASSENTE") ||
                        det[2].includes("RECUPERATA")); // Include tutte le varianti
                    const isAssenzaPrecedente =
                      typeof det[4] === "string" && det[4].startsWith("prev_");

                    // Debug: loggare i dati del recupero per capire cosa viene restituito
                    if (isAssenza && recupero) {
                      console.log("Dati recupero per ID", id, ":", recupero);
                      console.log("Data originale lezione:", det[0]);
                      console.log("ID Sessione corrente:", idSessione);
                      console.log(
                        "ID Sessione nel recupero:",
                        recupero.id_sessione,
                      );
                      console.log(
                        "recuperoHaSessioneCorretta:",
                        recuperoHaSessioneCorretta,
                      );
                      console.log(
                        "isRecuperataViaRecupero:",
                        isRecuperataViaRecupero,
                      );
                      console.log("isRecuperata:", isRecuperata);
                      console.log(
                        "Campi disponibili nel recupero:",
                        Object.keys(recupero),
                      );
                      console.log("recupero.data:", recupero.data);
                      console.log(
                        "recupero.data_recupero:",
                        recupero.data_recupero,
                      );
                      console.log(
                        "recupero.id_lezione_recupero:",
                        recupero.id_lezione_recupero,
                      );
                    }

                    // Usa una chiave unica: combina id recupero + id sessione + index per evitare duplicati
                    const uniqueKey = idSessione
                      ? `${id || "noId"}_sessione_${idSessione}`
                      : `${id || det[0]}_${index}`;

                    return (
                      <React.Fragment key={uniqueKey}>
                        <div style={infoDivPresenze}>
                          <div style={leftText}>
                            {/* Badge arancione per assenze da corso precedente */}
                            {isAssenzaPrecedente && (
                              <div
                                style={{
                                  display: "block",
                                  backgroundColor: "#F57C00",
                                  color: "white",
                                  padding: "4px 10px",
                                  borderRadius: "4px",
                                  fontSize: "12px",
                                  fontWeight: "bold",
                                  marginBottom: "6px",
                                  width: "fit-content",
                                }}
                              >
                                Da Classe Precedente
                              </div>
                            )}
                            <div>
                              <div>
                                <span style={{ fontWeight: "bold" }}>
                                  Assenza:
                                </span>{" "}
                                {(() => {
                                  // Formatta la data con ora se presente
                                  // Supporta multipli formati: "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DD HH:mm", "YYYY-MM-DD"
                                  // e anche formati da database con T separatore
                                  const rawDate = det[0];

                                  // Se la data è vuota o non valida
                                  if (
                                    !rawDate ||
                                    rawDate === "Data non disponibile"
                                  ) {
                                    return "Data non disponibile";
                                  }

                                  // Se la data è già nel formato DD/MM/YYYY, HH:mm (con virgola), mostrala direttamente
                                  if (
                                    rawDate.match(
                                      /^\d{2}\/\d{2}\/\d{4},?\s*\d{2}:\d{2}$/,
                                    )
                                  ) {
                                    return rawDate;
                                  }

                                  // Se la data è già nel formato DD/MM/YYYY (senza ora), mostrala direttamente
                                  if (rawDate.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
                                    return rawDate;
                                  }

                                  // Prova con moment usando multipli formati
                                  const dataMoment = moment(
                                    rawDate,
                                    [
                                      "YYYY-MM-DD HH:mm:ss",
                                      "YYYY-MM-DD HH:mm",
                                      "YYYY-MM-DDTHH:mm:ss",
                                      "YYYY-MM-DDTHH:mm",
                                      "YYYY-MM-DD",
                                      "DD/MM/YYYY, HH:mm",
                                      "DD/MM/YYYY HH:mm:ss",
                                      "DD/MM/YYYY HH:mm",
                                      "DD/MM/YYYY",
                                    ],
                                    true, // strict parsing
                                  );

                                  if (dataMoment.isValid()) {
                                    // Se ha l'ora (non è mezzanotte), mostrala
                                    if (
                                      dataMoment.format("HH:mm") !== "00:00"
                                    ) {
                                      return dataMoment.format(
                                        "DD/MM/YYYY, HH:mm",
                                      );
                                    }
                                    return dataMoment.format("DD/MM/YYYY");
                                  }

                                  // Fallback: prova con Date nativo (più permissivo)
                                  try {
                                    const nativeDate = new Date(
                                      rawDate.replace(" ", "T"),
                                    );
                                    if (!isNaN(nativeDate.getTime())) {
                                      return nativeDate
                                        .toLocaleString("it-IT", {
                                          day: "2-digit",
                                          month: "2-digit",
                                          year: "numeric",
                                          hour: "2-digit",
                                          minute: "2-digit",
                                        })
                                        .replace(", 00:00", "");
                                    }
                                  } catch (e) {
                                    console.error(
                                      "Errore parsing data:",
                                      e,
                                      rawDate,
                                    );
                                  }

                                  // Ultimo fallback: ritorna la data così com'è
                                  return rawDate;
                                })()}
                              </div>
                              {/* Mostra materia + (Prima/Seconda metà) da det[1] */}
                              {det[1] && det[1].trim() !== "" && (
                                <div style={{ marginTop: "2px" }}>
                                  {det[1].trim()}
                                </div>
                              )}
                            </div>
                          </div>
                          <div style={rightText}>
                            {/* Mostra i pulsanti di recupero per tutte le assenze (attuali e precedenti) */}
                            {isAssenza ? (
                              isRecuperata ? (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "1rem",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  <div>
                                    <b>
                                      Recuperata{" "}
                                      {(() => {
                                        // Se recuperata via recupero diretto, usa la data dal recupero
                                        if (isRecuperataViaRecupero) {
                                          // Prova diversi campi possibili per la data di recupero
                                          const dataRecupero =
                                            recupero.data ||
                                            recupero.data_recupero ||
                                            recupero.data_lezione_recupero;

                                          const noteRecupero =
                                            recupero.note ||
                                            recupero.note_recupero ||
                                            "";

                                          let recuperoText = "";

                                          if (dataRecupero) {
                                            recuperoText = `il: ${formatDate(
                                              dataRecupero,
                                            )}`;
                                          } else if (
                                            recupero.id_lezione_recupero
                                          ) {
                                            recuperoText = `(lezione di recupero registrata)`;
                                          }

                                          // Restituisci solo la data, le note saranno mostrate sotto
                                          return recuperoText;
                                        }
                                        return "";
                                      })()}
                                    </b>
                                    {/* Mostra sempre le note se presenti, indipendentemente dallo stato di recupero */}
                                    {(() => {
                                      // Cerca le note dal recupero o dalla proposta
                                      const noteRecupero =
                                        recupero?.note ||
                                        recupero?.note_recupero ||
                                        proposta?.note ||
                                        proposta?.note_recupero ||
                                        "";

                                      if (noteRecupero) {
                                        // Decodifica le entità HTML (come &#039; per gli apostrofi)
                                        const noteDecodificate = this.decodeHtmlEntities(
                                          noteRecupero,
                                        );

                                        return (
                                          <>
                                            <br />
                                            <div
                                              style={{
                                                color: "#d32f2f",
                                                fontSize: "14px",
                                                marginTop: "5px",
                                                padding: "8px",
                                                backgroundColor: "#ffebee",
                                                borderRadius: "4px",
                                                borderLeft: "3px solid #d32f2f",
                                              }}
                                            >
                                              📝 <strong>Note:</strong>{" "}
                                              {noteDecodificate}
                                            </div>
                                          </>
                                        );
                                      }
                                      return null;
                                    })()}
                                  </div>
                                  {/* Pulsanti per recupero effettuato */}
                                  <div
                                    style={{ display: "flex", gap: "0.5rem" }}
                                  >
                                    <button
                                      onClick={() =>
                                        this.openEditNoteRecuperoModal(id, det)
                                      }
                                      style={{
                                        transition: "all .3s",
                                        marginBottom: "10px",
                                        marginLeft: "5px",
                                        padding: "10px",
                                        backgroundColor: "#A00008",
                                        cursor: "pointer",
                                        borderRadius: "5px",
                                        border: "0px",
                                        color: "#ffffff",
                                        boxShadow: "0px 2px 5px lightgrey",
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "5px",
                                        height: "40px",
                                        minHeight: "40px",
                                      }}
                                      onMouseEnter={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                          "#530004";
                                        e.currentTarget.style.boxShadow =
                                          "0px 2px 5px darkgrey";
                                      }}
                                      onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor =
                                          "#A00008";
                                        e.currentTarget.style.boxShadow =
                                          "0px 2px 5px lightgrey";
                                      }}
                                    >
                                      <NoteIcon style={{ fontSize: 18 }} />
                                      Note
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "1rem",
                                    justifyContent: "flex-end",
                                  }}
                                >
                                  {/* Se non esiste un ID recupero, mostra il pulsante per generarlo */}
                                  {!id ? (
                                    // FLUSSO UNIFICATO: Stesso pulsante "Genera recupero" per assenze correnti E precedenti
                                    <FixButton
                                      title="Genera recupero"
                                      style={
                                        isAssenzaPrecedente
                                          ? {
                                              backgroundColor: "#FF9800",
                                              padding: "8px 12px",
                                              fontSize: "14px",
                                            }
                                          : undefined
                                      }
                                      callback={() =>
                                        this.generateRecuperoRequest(det)
                                      }
                                    />
                                  ) : (
                                    // Se esiste l'ID recupero, mostra i pulsanti normali
                                    <>
                                      {proposta?.result === "ok"
                                        ? (() => {
                                            // Otteniamo la data dalla proposta stessa, ora che il backend la restituisce
                                            console.log(
                                              "Proposta per ID",
                                              id,
                                              ":",
                                              proposta,
                                            ); // Debug per cambio data
                                            console.log(
                                              "Proposta per ID",
                                              id,
                                              "- stato:",
                                              proposta.stato,
                                              "richiesta_cambio_data:",
                                              proposta.richiesta_cambio_data,
                                              "result:",
                                              proposta.result,
                                            );
                                            console.log(
                                              "Campi disponibili nella proposta:",
                                              Object.keys(proposta),
                                            );
                                            console.log(
                                              "Note proposta:",
                                              proposta.note,
                                              proposta.note_recupero,
                                              proposta.notes,
                                            ); // Otteniamo la data della proposta dal backend
                                            let dataProposta =
                                              proposta.label_lezione_proposta;
                                            let corsoLabel =
                                              proposta.label_corso_proposto;

                                            // Aggiungi label sessione se presente
                                            let sessioneLabelProposta = "";
                                            if (
                                              proposta.id_sessione &&
                                              proposta.sessione_ordine !==
                                                undefined
                                            ) {
                                              sessioneLabelProposta =
                                                proposta.sessione_ordine === 0
                                                  ? " (Prima metà)"
                                                  : " (Seconda metà)";
                                            } else if (
                                              dataProposta &&
                                              (dataProposta.includes(
                                                "Prima metà",
                                              ) ||
                                                dataProposta.includes(
                                                  "Seconda metà",
                                                ))
                                            ) {
                                              // Pulisci emoji sbagliate e determina dalla label
                                              if (
                                                dataProposta.includes(
                                                  "Prima metà",
                                                )
                                              ) {
                                                sessioneLabelProposta =
                                                  " (Prima metà)";
                                                // Rimuovi la parte con emoji dalla dataProposta
                                                const idx = dataProposta.indexOf(
                                                  "Prima metà",
                                                );
                                                if (idx > 0)
                                                  dataProposta = dataProposta
                                                    .substring(0, idx)
                                                    .trim();
                                              } else if (
                                                dataProposta.includes(
                                                  "Seconda metà",
                                                )
                                              ) {
                                                sessioneLabelProposta =
                                                  " (Seconda metà)";
                                                // Rimuovi la parte con emoji dalla dataProposta
                                                const idx = dataProposta.indexOf(
                                                  "Seconda metà",
                                                );
                                                if (idx > 0)
                                                  dataProposta = dataProposta
                                                    .substring(0, idx)
                                                    .trim();
                                              }
                                            }

                                            // Fallback alla data originale se non presente nella proposta
                                            if (!dataProposta) {
                                              const lezione = this.props.details.find(
                                                (d) => d[3] === id,
                                              );
                                              if (lezione && lezione[0]) {
                                                dataProposta = lezione[0];
                                              }
                                            } // Gestione stati delle proposte:
                                            // stato 0 + richiesta_cambio_data = 1 → "Cambio data richiesto" (SOLO pulsante Proponi recupero)
                                            // stato 0 → "Recupero proposto" (pulsanti Accetta/Rifiuta per studente)
                                            // stato 1 → "Richiesta accettata" (pulsante Recupera per docente)
                                            if (
                                              proposta.stato === 0 &&
                                              proposta.richiesta_cambio_data ===
                                                1
                                            ) {
                                              // Caso: Studente ha richiesto cambio data
                                              return (
                                                <span
                                                  style={{
                                                    color: "#f58320",
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  Cambio data richiesto:{" "}
                                                  {formatDate(dataProposta) ||
                                                    ""}
                                                  {corsoLabel
                                                    ? ` (${corsoLabel})`
                                                    : ""}
                                                  <br />
                                                  <small
                                                    style={{
                                                      color: "#666",
                                                      fontWeight: "normal",
                                                    }}
                                                  >
                                                    In attesa di nuova proposta
                                                    dal docente
                                                  </small>
                                                </span>
                                              );
                                            } else if (proposta.stato === 0) {
                                              // Caso: Proposta normale in attesa di risposta studente
                                              const noteRecupero =
                                                recupero?.note ||
                                                recupero?.note_recupero ||
                                                proposta?.note ||
                                                proposta?.note_recupero ||
                                                "";

                                              return (
                                                <div>
                                                  <div>
                                                    <strong>
                                                      Recupero proposto:
                                                    </strong>{" "}
                                                    {formatDate(dataProposta) ||
                                                      ""}
                                                  </div>
                                                  <div
                                                    style={{ marginTop: "4px" }}
                                                  >
                                                    {sessioneLabelProposta && (
                                                      <span>
                                                        {sessioneLabelProposta.trim()}{" "}
                                                      </span>
                                                    )}
                                                    {corsoLabel &&
                                                      corsoLabel !==
                                                        "stesso corso" && (
                                                        <span>
                                                          ({corsoLabel})
                                                        </span>
                                                      )}
                                                  </div>
                                                  <small
                                                    style={{
                                                      color: "#666",
                                                      display: "block",
                                                      marginTop: "4px",
                                                    }}
                                                  >
                                                    In attesa di risposta dallo
                                                    studente
                                                  </small>
                                                  {noteRecupero && (
                                                    <>
                                                      <br />
                                                      <div
                                                        style={{
                                                          color: "#d32f2f",
                                                          fontSize: "13px",
                                                          marginTop: "5px",
                                                          padding: "6px",
                                                          backgroundColor:
                                                            "#ffebee",
                                                          borderRadius: "4px",
                                                          borderLeft:
                                                            "3px solid #d32f2f",
                                                        }}
                                                      >
                                                        {" "}
                                                        <strong>
                                                          Note:
                                                        </strong>{" "}
                                                        {this.decodeHtmlEntities(
                                                          noteRecupero,
                                                        )}
                                                      </div>
                                                    </>
                                                  )}
                                                </div>
                                              );
                                            } else if (proposta.stato === 1) {
                                              // Caso: Proposta accettata dallo studente
                                              const noteRecupero =
                                                recupero?.note ||
                                                recupero?.note_recupero ||
                                                proposta?.note ||
                                                proposta?.note_recupero ||
                                                "";

                                              return (
                                                <div>
                                                  <span
                                                    style={{
                                                      color: "#28a745",
                                                      fontWeight: "bold",
                                                    }}
                                                  >
                                                    Richiesta accettata:{" "}
                                                    {formatDate(dataProposta) ||
                                                      ""}
                                                    {corsoLabel
                                                      ? ` (${corsoLabel})`
                                                      : ""}
                                                    <br />
                                                    <small
                                                      style={{
                                                        color: "#28a745",
                                                        fontWeight: "normal",
                                                      }}
                                                    >
                                                      Pronto per il recupero
                                                    </small>
                                                  </span>
                                                  {noteRecupero && (
                                                    <>
                                                      <br />
                                                      <div
                                                        style={{
                                                          color: "#d32f2f",
                                                          fontSize: "13px",
                                                          marginTop: "5px",
                                                          padding: "6px",
                                                          backgroundColor:
                                                            "#ffebee",
                                                          borderRadius: "4px",
                                                          borderLeft:
                                                            "3px solid #d32f2f",
                                                        }}
                                                      >
                                                        {" "}
                                                        <strong>
                                                          Note:
                                                        </strong>{" "}
                                                        {this.decodeHtmlEntities(
                                                          noteRecupero,
                                                        )}
                                                      </div>
                                                    </>
                                                  )}
                                                </div>
                                              );
                                            } else if (proposta.stato === 2) {
                                              // Caso: Proposta rifiutata dallo studente
                                              const noteRecupero =
                                                recupero?.note ||
                                                recupero?.note_recupero ||
                                                proposta?.note ||
                                                proposta?.note_recupero ||
                                                "";

                                              return (
                                                <div>
                                                  <span
                                                    style={{
                                                      color: "#dc3545",
                                                      fontWeight: "bold",
                                                    }}
                                                  >
                                                    Proposta rifiutata:{" "}
                                                    {formatDate(dataProposta) ||
                                                      ""}
                                                    {corsoLabel
                                                      ? ` (${corsoLabel})`
                                                      : ""}
                                                    <br />
                                                    <small
                                                      style={{
                                                        color: "#666",
                                                        fontWeight: "normal",
                                                      }}
                                                    >
                                                      È possibile proporre una
                                                      nuova data
                                                    </small>
                                                  </span>
                                                  {noteRecupero && (
                                                    <>
                                                      <br />
                                                      <div
                                                        style={{
                                                          color: "#d32f2f",
                                                          fontSize: "13px",
                                                          marginTop: "5px",
                                                          padding: "6px",
                                                          backgroundColor:
                                                            "#ffebee",
                                                          borderRadius: "4px",
                                                          borderLeft:
                                                            "3px solid #d32f2f",
                                                        }}
                                                      >
                                                        {" "}
                                                        <strong>
                                                          Note:
                                                        </strong>{" "}
                                                        {this.decodeHtmlEntities(
                                                          noteRecupero,
                                                        )}
                                                      </div>
                                                    </>
                                                  )}
                                                </div>
                                              );
                                            } else {
                                              // Caso: Stato sconosciuto
                                              return (
                                                <span
                                                  style={{ color: "#6c757d" }}
                                                >
                                                  Stato sconosciuto:{" "}
                                                  {proposta.stato}
                                                </span>
                                              );
                                            }
                                          })()
                                        : null}

                                      {/* Pulsanti azione - sempre dentro lo stesso contenitore per dimensioni uniformi */}
                                      <div
                                        style={{
                                          display: "flex",
                                          gap: "0.5rem",
                                          marginTop: "0.5rem",
                                          flexWrap: "wrap",
                                        }}
                                      >
                                        {/* Mostra pulsante "Proponi recupero" quando NON c'è una proposta o quando c'è richiesta cambio data */}
                                        {(!proposta ||
                                          proposta.result !== "ok" ||
                                          (proposta?.stato === 0 &&
                                            proposta?.richiesta_cambio_data ===
                                              1)) && (
                                          <button
                                            onClick={() =>
                                              this.openEditProponiRecuperoModal(
                                                id,
                                                det,
                                              )
                                            }
                                            style={{
                                              transition: "all .3s",
                                              marginBottom: "10px",
                                              marginLeft: "5px",
                                              padding: "10px",
                                              backgroundColor: "#A00008",
                                              cursor: "pointer",
                                              borderRadius: "5px",
                                              border: "0px",
                                              color: "#ffffff",
                                              boxShadow:
                                                "0px 2px 5px lightgrey",
                                              display: "flex",
                                              alignItems: "center",
                                              gap: "5px",
                                            }}
                                            onMouseEnter={(e) => {
                                              e.currentTarget.style.backgroundColor =
                                                "#530004";
                                              e.currentTarget.style.boxShadow =
                                                "0px 2px 5px darkgrey";
                                            }}
                                            onMouseLeave={(e) => {
                                              e.currentTarget.style.backgroundColor =
                                                "#A00008";
                                              e.currentTarget.style.boxShadow =
                                                "0px 2px 5px lightgrey";
                                            }}
                                          >
                                            <EventIcon
                                              style={{ fontSize: 18 }}
                                            />
                                            Proponi
                                          </button>
                                        )}

                                        {/* Mostra "Recupera" se NON è stato già recuperato */}
                                        {!recupero?.id_lezione_recupero &&
                                          (!proposta ||
                                            proposta.stato !== 2) && (
                                            <button
                                              onClick={() =>
                                                this.openEditRecuperoModal(
                                                  id,
                                                  det,
                                                )
                                              }
                                              style={{
                                                transition: "all .3s",
                                                marginBottom: "10px",
                                                marginLeft: "5px",
                                                padding: "10px",
                                                backgroundColor: "#A00008",
                                                cursor: "pointer",
                                                borderRadius: "5px",
                                                border: "0px",
                                                color: "#ffffff",
                                                boxShadow:
                                                  "0px 2px 5px lightgrey",
                                                display: "flex",
                                                alignItems: "center",
                                                gap: "5px",
                                                height: "40px",
                                                minHeight: "40px",
                                              }}
                                              onMouseEnter={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                  "#530004";
                                                e.currentTarget.style.boxShadow =
                                                  "0px 2px 5px darkgrey";
                                              }}
                                              onMouseLeave={(e) => {
                                                e.currentTarget.style.backgroundColor =
                                                  "#A00008";
                                                e.currentTarget.style.boxShadow =
                                                  "0px 2px 5px lightgrey";
                                              }}
                                            >
                                              <CheckCircleIcon
                                                style={{ fontSize: 18 }}
                                              />
                                              Recupera
                                            </button>
                                          )}

                                        <button
                                          onClick={() =>
                                            this.openEditNoteRecuperoModal(
                                              id,
                                              det,
                                            )
                                          }
                                          style={{
                                            transition: "all .3s",
                                            marginBottom: "10px",
                                            marginLeft: "5px",
                                            padding: "10px",
                                            backgroundColor: "#A00008",
                                            cursor: "pointer",
                                            borderRadius: "5px",
                                            border: "0px",
                                            color: "#ffffff",
                                            boxShadow: "0px 2px 5px lightgrey",
                                            display: "flex",
                                            alignItems: "center",
                                            gap: "5px",
                                            height: "40px",
                                            minHeight: "40px",
                                          }}
                                          onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "#530004";
                                            e.currentTarget.style.boxShadow =
                                              "0px 2px 5px darkgrey";
                                          }}
                                          onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor =
                                              "#A00008";
                                            e.currentTarget.style.boxShadow =
                                              "0px 2px 5px lightgrey";
                                          }}
                                        >
                                          <NoteIcon style={{ fontSize: 18 }} />
                                          Note
                                        </button>
                                      </div>
                                    </>
                                  )}
                                </div>
                              )
                            ) : (
                              <b>{det[2]}</b>
                            )}
                          </div>
                        </div>
                        <div style={separatorStyle}></div>
                      </React.Fragment>
                    );
                  }); // Chiusura del map
                })()}
              {this.state.showEditRecupero && (
                <EditRecuperoLezioneWidget
                  onClose={() => this.setState({ showEditRecupero: false })}
                  studente={this.props.studente}
                  id_recupero_lezione={this.state.det}
                  id_sessione={this.state.currentIdSessione}
                  initialNote={this.state.currentNote || ""}
                  onlyNotes={this.state.editRecuperoOnlyNotes}
                  reloadRecupero={this.reloadRecupero}
                />
              )}{" "}
              {this.state.showPropostaRecupero && (
                <PropostaRecuperoLezione
                  onClose={() => this.setState({ showPropostaRecupero: false })}
                  studente={this.props.studente}
                  id_recupero_lezione={this.state.det}
                  id_sessione={this.state.currentIdSessione}
                  reloadRecupero={this.reloadRecupero}
                />
              )}
              {/* Tasto stampa riepilogo presenze - solo nella sezione dettagli presenza */}
              {this.state.openDetails && (
                <div
                  style={{
                    marginTop: "20px",
                    paddingTop: "15px",
                    borderTop: "1px solid #BDBDBD",
                    textAlign: "center",
                  }}
                >
                  {" "}
                  <FixButton
                    title="STAMPA RIEPILOGO"
                    callback={() => this.printAttendanceSummary()}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}

const mainClass = {
  position: "fixed",
  top: "17%",
  bottom: "10%",
  left: "20%",
  width: "60%",
  border: "1px solid black",
  background: "#ffffff",
  padding: "2%",
  overflow: "scroll",
  textAlign: "center",
};

const container = {
  position: "fixed",
  width: "100%",
  height: "100%",
  top: 0,
  left: 0,
  background: "#ffffff88",
};

const title = {
  position: "relative",
  textAlign: "center",
  width: "100%",
  fontWeight: "bold",
  fontSize: "1.5rem",
  color: "#BDBDBD",
};

const subtitle = {
  position: "relative",
  textAlign: "center",
  width: "100%",
  fontWeight: "bold",
  fontSize: "0.7rem",
  color: "red",
};

const closeBtn = {
  position: "absolute",
  right: "1%",
  top: "1%",
  cursor: "pointer",
};

const selectionDiv = {
  marginLeft: "10%",
  display: "flex",
  height: "40px",
  width: "80%",
  maxWidth: "80%",
  marginTop: "25px",
  marginBottom: "20px",
  cursor: "pointer",
};

const selectionButton = {
  flex: 1,
  textAlign: "center",
};

const infoDiv = {
  marginLeft: "10%",
  display: "flex",
  minHeight: "15px",
  width: "80%",
  maxWidth: "80%",
  marginTop: "5px",
};

const examDescDiv = {
  flex: 1,
  textAlign: "start",
  width: "100%",
};

const examAvgDiv = {
  flex: 1,
  textAlign: "end",
  alignContent: "flex-end",
  alignItems: "center",
  width: "100%",
};

const infoDivPresenze = {
  marginLeft: "10%",
  display: "flex",
  minHeight: "auto", // Cambiato da 15px per permettere espansione
  width: "80%",
  maxWidth: "80%",
  marginTop: "5px",
  justifyContent: "space-between",
  alignItems: "flex-start", // Allinea i figli in alto, non al centro
};

const leftText = {
  textAlign: "start",
  flex: 1, // Permette al contenitore di espandersi
  overflow: "visible", // Assicura che il contenuto non sia nascosto
};

const rightText = {
  textAlign: "end",
  marginLeft: "10px", // Spazio tra leftText e rightText
};

const separatorStyle = {
  height: "1px",
  width: "80%",
  marginTop: "8px",
  marginLeft: "10%",
  backgroundColor: "#D8D8D8",
};
